/**
 * CheckoutFunnelAdmin
 *
 * Real-time visibility into checkout intents:
 * - Aggregate funnel stats (started → completed → abandoned) per template + plan
 * - Detail rows for individual intents
 * - Filters by status and look-back window
 *
 * Backed by /api/admin/checkout-funnel + /api/admin/recovery-leads
 * (unified Stripe + MercadoPago payment attempts for recovery).
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, Info, Loader2, RefreshCw, TrendingDown, XCircle, CreditCard, Timer, AlertCircle } from "lucide-react";
import type { IntentRow, FunnelGroup, ViewGroup, ErrorBreakdown } from "../../../api/admin/checkout-funnel";

// ─── Types — single source of truth: shared with the backend handler ─────────

interface FunnelResponse {
  window_hours: number;
  status_filter: string;
  generated_at: string;
  totals: {
    total: number;
    started: number;
    completed: number;
    failed: number;
    abandoned: number;
    conversion_rate: number;
    abandonment_rate: number;
  };
  provider_totals?: { provider: string; total: number; completed: number; conversion_rate: number }[];
  view_totals?: { views: number; unique_visitors: number };
  view_groups?: ViewGroup[];
  /** DB health for template_views (migration 044+). */
  template_views_status?: { ok: true } | { ok: false; error: string };
  groups: FunnelGroup[];
  rows: IntentRow[];
  error_breakdown?: ErrorBreakdown[];
  avg_time_to_complete_s?: number | null;
}

type RecoveryLeadsResponse = {
  generated_at: string;
  recovery_checkout_provider: string;
  totals: {
    unique_emails: number;
    recoverable_now_2_24h: number;
    by_provider_on_directory: {
      stripe: number;
      mercadopago: number;
      other: number;
    };
  };
  recoverable_window: {
    raw: number;
    after_dedupe: number;
    leads: Array<{
      customer_email: string;
      provider: string;
      country: string | null;
      flow_name: string | null;
      status: string;
      created_at: string;
    }>;
  };
};

const POLL_MS = 15_000;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

function fmtAmount(amount: number | null, currency: string | null, provider?: string | null): string {
  if (amount == null) return "—";
  const c = (currency || "ARS").toUpperCase();
  // Provider-driven: Stripe stores cents (USD), MP stores major units (ARS pesos).
  // The amount_cents column in premium_checkout_intents currently mirrors amount
  // (pre-existing schema quirk in upsertPremiumCheckoutIntentInternal), so we
  // can't blindly trust either column — use the provider as the unit indicator.
  const value = provider === "stripe" ? amount / 100 : amount;
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: c }).format(value);
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function statusColor(status: string): string {
  switch (status) {
    case "completed": return "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40";
    case "failed":    return "text-red-600 bg-red-50 dark:bg-red-950/40";
    case "started":   return "text-amber-600 bg-amber-50 dark:bg-amber-950/40";
    default:          return "text-muted-foreground bg-muted";
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function CheckoutFunnelAdmin() {
  const { getAuthHeaders } = useAdminAuth();
  const [data, setData] = useState<FunnelResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hours, setHours] = useState(24);
  const [statusFilter, setStatusFilter] = useState<"all" | "started" | "completed" | "failed">("all");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [secsSince, setSecsSince] = useState(0);
  const [recovery, setRecovery] = useState<RecoveryLeadsResponse | null>(null);
  const getAuthHeadersRef = useRef(getAuthHeaders);
  getAuthHeadersRef.current = getAuthHeaders;

  const fetchFunnel = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const headers = await getAuthHeadersRef.current();
      const res = await fetch(`/api/admin/checkout-funnel?hours=${hours}&status=${statusFilter}&limit=200`, { headers });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status} — ${body.slice(0, 200)}`);
      }
      const json = (await res.json()) as FunnelResponse;
      setData(json);
      setLastUpdated(new Date());
      if (silent) setError(null);
      // Unified recovery leads (Stripe + MP) — best-effort, don't block funnel UI.
      void fetch("/api/admin/recovery-leads?days=90&limit=200&onlyRecoverable=1", {
        headers,
      })
        .then(async (r) => {
          if (!r.ok) return;
          setRecovery((await r.json()) as RecoveryLeadsResponse);
        })
        .catch(() => {});
    } catch (err) {
      if (!silent) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [hours, statusFilter]);

  useEffect(() => { void fetchFunnel(); }, [fetchFunnel]);

  useEffect(() => {
    const id = window.setInterval(() => { void fetchFunnel({ silent: true }); }, POLL_MS);
    return () => window.clearInterval(id);
  }, [fetchFunnel]);

  useEffect(() => {
    setSecsSince(0);
    const id = window.setInterval(() => setSecsSince((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [lastUpdated]);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <AdminPageHeader
        description="Intents started → completed → abandoned (agregado por template/plan). Feed en vivo → Live funnel. Emails exportables → Leads recovery."
        actions={
          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 text-xs text-muted-foreground sm:flex">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span>
                En vivo · cada {POLL_MS / 1000}s
                {lastUpdated && <> · hace <strong className="tabular-nums">{secsSince}s</strong></>}
              </span>
            </div>
            <Button onClick={() => void fetchFunnel()} disabled={loading} variant="outline" size="sm" type="button">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 text-xs">
          <span className="text-muted-foreground mr-2">Ventana:</span>
          {[6, 24, 72, 168].map((h) => (
            <button
              key={h}
              onClick={() => setHours(h)}
              className={`rounded px-2 py-1 ${hours === h ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}
            >
              {h < 24 ? `${h}h` : `${h / 24}d`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-muted-foreground mr-2">Estado:</span>
          {(["all", "started", "completed", "failed"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`rounded px-2 py-1 ${statusFilter === s ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}
            >
              {s === "all" ? "Todos" : s === "started" ? "Iniciados" : s === "completed" ? "Completados" : "Fallidos"}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <Card className="border-red-500/30 bg-red-50/40 dark:bg-red-950/20">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600" />
              <div className="text-sm">
                <p className="font-medium text-red-700 dark:text-red-300">Error cargando datos</p>
                <code className="mt-1 block text-xs text-red-600 dark:text-red-400">{error}</code>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : data ? (
        <>
          {/* Top-line totals */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="text-xs">Total intents</CardDescription>
                <CardTitle className="text-2xl tabular-nums">{data.totals.total}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1 text-xs">
                  <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Completados
                </CardDescription>
                <CardTitle className="text-2xl tabular-nums text-emerald-600">{data.totals.completed}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1 text-xs">
                  <Clock className="h-3 w-3 text-amber-600" /> Iniciados
                </CardDescription>
                <CardTitle className="text-2xl tabular-nums text-amber-600">{data.totals.started}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1 text-xs">
                  <TrendingDown className="h-3 w-3 text-orange-600" /> Abandonados
                </CardDescription>
                <CardTitle className="text-2xl tabular-nums text-orange-600">{data.totals.abandoned}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-1 text-xs">
                  <XCircle className="h-3 w-3 text-red-600" /> Fallidos
                </CardDescription>
                <CardTitle className="text-2xl tabular-nums text-red-600">{data.totals.failed}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Unified recovery (Stripe + MercadoPago) */}
          {recovery && (
            <Card className="border-amber-500/30 bg-amber-500/[0.03]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recovery unificado (Stripe + MercadoPago)</CardTitle>
                <CardDescription>
                  Intentos en <code className="text-[10px]">premium_checkout_intents</code>. El mail de
                  recovery siempre abre checkout Stripe (membresía), aunque el intento original haya
                  sido MP. Ventana cron: 2–24h.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-lg border bg-background/60 p-3">
                    <div className="text-2xl font-bold tabular-nums">
                      {recovery.totals.recoverable_now_2_24h}
                    </div>
                    <div className="text-[11px] text-muted-foreground">Recuperables ahora</div>
                  </div>
                  <div className="rounded-lg border bg-background/60 p-3">
                    <div className="text-2xl font-bold tabular-nums">
                      {recovery.totals.unique_emails}
                    </div>
                    <div className="text-[11px] text-muted-foreground">Emails únicos (90d unpaid)</div>
                  </div>
                  <div className="rounded-lg border bg-background/60 p-3">
                    <div className="text-2xl font-bold tabular-nums">
                      {recovery.totals.by_provider_on_directory.stripe}
                    </div>
                    <div className="text-[11px] text-muted-foreground">Con intent Stripe</div>
                  </div>
                  <div className="rounded-lg border bg-background/60 p-3">
                    <div className="text-2xl font-bold tabular-nums">
                      {recovery.totals.by_provider_on_directory.mercadopago}
                    </div>
                    <div className="text-[11px] text-muted-foreground">Con intent MercadoPago</div>
                  </div>
                </div>
                {recovery.recoverable_window.leads.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded-md border">
                    <table className="w-full text-left text-xs">
                      <thead className="sticky top-0 bg-muted/80">
                        <tr>
                          <th className="px-2 py-1.5 font-medium">Email</th>
                          <th className="px-2 py-1.5 font-medium">Provider</th>
                          <th className="px-2 py-1.5 font-medium">Flow</th>
                          <th className="px-2 py-1.5 font-medium">País</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recovery.recoverable_window.leads.slice(0, 20).map((l) => (
                          <tr key={`${l.customer_email}-${l.created_at}`} className="border-t">
                            <td className="px-2 py-1 font-mono text-[11px]">{l.customer_email}</td>
                            <td className="px-2 py-1">{l.provider}</td>
                            <td className="px-2 py-1 text-muted-foreground">{l.flow_name || "—"}</td>
                            <td className="px-2 py-1">{l.country || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[10px] text-muted-foreground">
                    API: <code>GET /api/admin/recovery-leads</code> · recovery checkout ={" "}
                    <strong>{recovery.recovery_checkout_provider}</strong>
                  </p>
                  <Button variant="secondary" size="sm" className="h-8" asChild>
                    <Link to="/admin/recovery-leads">Ver todos los emails →</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Conversion rate banner */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Tasa de conversión</p>
                  <p className="text-3xl font-bold tabular-nums text-emerald-600">{fmtPct(data.totals.conversion_rate)}</p>
                  <p className="text-xs text-muted-foreground">completados / total</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tasa de abandono</p>
                  <p className="text-3xl font-bold tabular-nums text-orange-600">{fmtPct(data.totals.abandonment_rate)}</p>
                  <p className="text-xs text-muted-foreground">started + sin pago tras 2h</p>
                </div>
                <div>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Timer className="h-3 w-3" /> Tiempo promedio checkout
                  </p>
                  <p className="text-3xl font-bold tabular-nums text-sky-600">{fmtDuration(data.avg_time_to_complete_s)}</p>
                  <p className="text-xs text-muted-foreground">desde inicio hasta completado</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Provider breakdown */}
          {data.provider_totals && data.provider_totals.length > 0 && (
            <div className="grid gap-3 md:grid-cols-2">
              {data.provider_totals.map((pt) => (
                <Card key={pt.provider}>
                  <CardHeader className="pb-2">
                    <CardDescription className="flex items-center gap-1 text-xs">
                      <CreditCard className="h-3 w-3" />
                      {pt.provider === "stripe" ? "💳 Stripe" : pt.provider === "mercadopago" ? "🇦🇷 MercadoPago" : pt.provider}
                    </CardDescription>
                    <div className="flex items-end justify-between">
                      <CardTitle className="text-2xl tabular-nums">{pt.total}</CardTitle>
                      <div className="text-right">
                        <p className="text-sm font-semibold tabular-nums text-emerald-600">{pt.completed} completados</p>
                        <p className="text-xs text-muted-foreground">{fmtPct(pt.conversion_rate)} conv.</p>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}

          {/* Top of funnel: VIEWS — server-side recorded from BioTemplatePremium* mounts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>Vistas de templates (top of funnel)</span>
                {data.view_totals && (
                  <span className="text-xs font-normal text-muted-foreground tabular-nums">
                    {data.view_totals.views} vistas · {data.view_totals.unique_visitors} únicos
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                Cada vez que un usuario carga BioTemplatePremium* registramos una fila en
                <code className="mx-1 font-mono text-[10px]">template_views</code>.
                Cruzando con <code className="font-mono text-[10px]">premium_checkout_intents</code> obtenemos
                el funnel real: <strong>vistas → click checkout → completado</strong>.
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {data.template_views_status && !data.template_views_status.ok && (
                <div className="mb-4 rounded border border-red-500/40 bg-red-500/5 p-3 text-xs text-red-200">
                  <p className="font-semibold text-red-300">No se pudo leer template_views</p>
                  <code className="mt-1 block break-all text-[11px] text-red-400">
                    {(data.template_views_status as { ok: false; error: string }).error}
                  </code>
                  <p className="mt-2 text-red-300/90">
                    Suele indicar que la migración <code className="font-mono">044_template_views.sql</code> no corre en esta base,
                    o un error de permisos/schema. Revisá integraciones → self-test de DB o ejecutá migraciones en producción.
                  </p>
                </div>
              )}
              {!data.view_groups || data.view_groups.length === 0 ? (
                <div className="space-y-3">
                  <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
                    <p className="flex items-start gap-2 font-medium text-amber-100">
                      <Info className="mt-0.5 h-4 w-4 shrink-0" />
                      Sin vistas de template en esta ventana ({data.window_hours}h): no siempre es un fallo.
                    </p>
                    <ul className="mt-2 list-inside list-disc space-y-1 text-amber-200/90">
                      <li>
                        <strong>Poco tráfico:</strong> si nadie abrió rutas con{" "}
                        <code className="font-mono text-[10px]">BioTemplatePremium*</code>, el contador sigue en 0.
                      </li>
                      <li>
                        <strong>Tracking:</strong> las vistas se guardan vía{" "}
                        <code className="font-mono text-[10px]">/api/track-template-view</code> al montar el template (best-effort,
                        no bloquea la UI).
                      </li>
                      <li>
                        <strong>Comparar:</strong> en{" "}
                        <Link to="/admin/live-funnel" className="underline hover:text-amber-50">
                          Live funnel
                        </Link>{" "}
                        deberías ver actividad si hay eventos en <code className="font-mono text-[10px]">funnel_events</code>.
                      </li>
                      <li>
                        <strong>Infra:</strong>{" "}
                        <Link to="/admin/integraciones" className="underline hover:text-amber-50">
                          Integraciones
                        </Link>{" "}
                        → self-test incluye fila <code className="font-mono text-[10px]">template_views</code> y conteo 24h.
                      </li>
                    </ul>
                    {data.template_views_status?.ok === true && (
                      <p className="mt-2 border-t border-amber-500/20 pt-2 text-amber-300/80">
                        La tabla existe y responde; si hay visitantes reales en premium y sigue en 0 tras unos minutos, revisá red
                        (bloqueos a la API) o que el usuario no esté en otra plantilla.
                      </p>
                    )}
                    {data.template_views_status && !data.template_views_status.ok && (
                      <p className="mt-2 font-medium text-amber-100">
                        Arribá: error de base en <code className="font-mono">template_views</code> — prioridad corregir migración /
                        schema.
                      </p>
                    )}
                    {!data.template_views_status && (
                      <p className="mt-2 text-amber-300/80">
                        Si este panel es antiguo (deploy previo), actualizá: el API ahora indica si la tabla falló o solo no hay datos.
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2 pr-4">Template</th>
                      <th className="pb-2 pr-4">Variant</th>
                      <th className="pb-2 pr-4">Flow</th>
                      <th className="pb-2 pr-4">País</th>
                      <th className="pb-2 pr-4 text-right">Vistas</th>
                      <th className="pb-2 pr-4 text-right">Únicos</th>
                      <th className="pb-2 pr-4 text-right text-amber-600">→ Click checkout</th>
                      <th className="pb-2 pr-4 text-right text-emerald-600">→ Completado</th>
                      <th className="pb-2 pr-4 text-right">CTR view→click</th>
                      <th className="pb-2 text-right">CTR view→pago</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.view_groups.map((v, i) => (
                      <tr key={`${v.template}-${v.flow_name}-${v.country}-${i}`} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 pr-4 font-medium">{v.template}</td>
                        <td className="py-2 pr-4 text-xs text-muted-foreground">{v.template_variant || "—"}</td>
                        <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{v.flow_name || "—"}</td>
                        <td className="py-2 pr-4">{v.country || "—"}</td>
                        <td className="py-2 pr-4 text-right tabular-nums font-semibold">{v.views}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">{v.unique_visitors}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-amber-600">{v.intents_started}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-emerald-600">{v.intents_completed}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">{fmtPct(v.view_to_intent_rate)}</td>
                        <td className="py-2 text-right tabular-nums font-semibold">{fmtPct(v.view_to_completed_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Per-template breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Funnel por template + plan</CardTitle>
              <CardDescription>Agrupado por flow_name + plan_tier. Ordenado por volumen.</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="pb-2 pr-4">Template</th>
                    <th className="pb-2 pr-4">Flow</th>
                    <th className="pb-2 pr-4">Plan</th>
                    <th className="pb-2 pr-4">Provider</th>
                    <th className="pb-2 pr-4 text-right">Total</th>
                    <th className="pb-2 pr-4 text-right text-emerald-600">✓ Completados</th>
                    <th className="pb-2 pr-4 text-right text-amber-600">… Iniciados</th>
                    <th className="pb-2 pr-4 text-right text-orange-600">⤓ Abandonados</th>
                    <th className="pb-2 pr-4 text-right text-red-600">✗ Fallidos</th>
                    <th className="pb-2 pr-4 text-right">Conv.</th>
                    <th className="pb-2 text-right">Recovery</th>
                  </tr>
                </thead>
                <tbody>
                  {data.groups.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="py-8 text-center text-muted-foreground">
                        Sin intents en la ventana seleccionada.
                      </td>
                    </tr>
                  ) : (
                    data.groups.map((g) => (
                      <tr key={`${g.flow_name}-${g.plan_tier}-${g.provider}`} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 pr-4 font-medium">{g.template}</td>
                        <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{g.flow_name}</td>
                        <td className="py-2 pr-4 capitalize">{g.plan_tier}</td>
                        <td className="py-2 pr-4">
                          <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                            {g.provider === "stripe" ? "💳 Stripe" : g.provider === "mercadopago" ? "🇦🇷 MP" : g.provider}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums font-semibold">{g.total}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-emerald-600">{g.completed}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-amber-600">{g.started}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-orange-600">{g.abandoned}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-red-600">{g.failed}</td>
                        <td className="py-2 pr-4 text-right tabular-nums font-semibold">
                          {fmtPct(g.conversion_rate)}
                        </td>
                        <td className="py-2 text-right tabular-nums text-muted-foreground">{g.recovery_sent}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Error breakdown */}
          {data.error_breakdown && data.error_breakdown.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-red-500">
                  <AlertCircle className="h-4 w-4" /> Motivos de fallo en checkout
                </CardTitle>
                <CardDescription>
                  Top errores en intents con status <code className="font-mono text-[10px]">failed</code> en la ventana seleccionada.
                  Usá esto para detectar problemas recurrentes con MP o Stripe.
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2 pr-4">Provider</th>
                      <th className="pb-2 pr-4">Mensaje de error</th>
                      <th className="pb-2 text-right">Ocurrencias</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.error_breakdown.map((e, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 pr-4">
                          <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                            {e.provider === "stripe" ? "💳 Stripe" : e.provider === "mercadopago" ? "🇦🇷 MP" : e.provider}
                          </span>
                        </td>
                        <td className="py-2 pr-4 max-w-[420px] truncate text-red-600 dark:text-red-400 text-xs" title={e.error_message}>
                          {e.error_message}
                        </td>
                        <td className="py-2 text-right tabular-nums font-semibold text-red-600">{e.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Detail rows */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Intents recientes</CardTitle>
              <CardDescription>Últimos {data.rows.length} registros (filtro: {statusFilter}, {hours}h)</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-3">Hora</th>
                    <th className="pb-2 pr-3">Estado</th>
                    <th className="pb-2 pr-3">Provider</th>
                    <th className="pb-2 pr-3">Email</th>
                    <th className="pb-2 pr-3">Plan</th>
                    <th className="pb-2 pr-3">Flow</th>
                    <th className="pb-2 pr-3">País</th>
                    <th className="pb-2 pr-3">Monto</th>
                    <th className="pb-2 pr-3">Recovery</th>
                    <th className="pb-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-8 text-center text-muted-foreground">Sin intents.</td>
                    </tr>
                  ) : (
                    data.rows.map((r) => (
                      <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-1.5 pr-3 tabular-nums text-muted-foreground">{fmtTime(r.created_at)}</td>
                        <td className="py-1.5 pr-3">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${statusColor(r.status)}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="py-1.5 pr-3 capitalize">{r.provider}</td>
                        <td className="py-1.5 pr-3 max-w-[200px] truncate" title={r.customer_email ?? ""}>
                          {r.customer_email ?? <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="py-1.5 pr-3 capitalize">{r.plan_tier ?? "—"}</td>
                        <td className="py-1.5 pr-3 font-mono text-[10px] text-muted-foreground">{r.flow_name ?? "—"}</td>
                        <td className="py-1.5 pr-3">{r.country ?? "—"}</td>
                        <td className="py-1.5 pr-3 tabular-nums">{fmtAmount(r.amount, r.currency, r.provider)}</td>
                        <td className="py-1.5 pr-3">
                          {r.recovery_email_sent ? (
                            <span className="text-emerald-600">✓</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-1.5 max-w-[300px] truncate text-red-600" title={r.error_message ?? ""}>
                          {r.error_message ?? ""}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
